import React, { useState } from "react";

import { Drawer, Toolbar, Stack, Box, Typography, styled } from "@mui/material";
import { Award03, Settings1 } from "components/icons";
import ChallengeStatusCard from "components/modules/ChallengeStatusCard";
import SubscriptionBadge from "components/modules/SubscriptionBadge";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import { useAPIBadgeTypes } from "hooks/useAPI";
import { useIsSmallScreen } from "hooks/useWindowSize";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ISidebarItems } from "types/types";
import { getLocalStorageBooleanValue } from "utils";
import { renderNumber } from "utils/formatter";

import AccordionItem from "../AccordionItem";
import CreditBadge from "../CreditBadge";
import MenuItem from "../MenuItem";

interface Props {
  sidebarItems: ISidebarItems[];
  drawerOpen: boolean;
  drawerWidth: number;
  onItemClick: () => void;
  userLoggedIn: boolean;
  botsList: {
    enabledList: React.ReactNode[];
    disabledList: React.ReactNode[];
  };
}

const MyAccountButton = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  cursor: "pointer",
}));

const SidebarMenu: React.FC<Props> = ({
  sidebarItems,
  drawerOpen,
  drawerWidth,
  onItemClick,
  userLoggedIn,
  botsList,
}) => {
  const { colors } = useMUIThemeModeContext();
  const navigate = useNavigate();
  const isSmallScreen = useIsSmallScreen();
  const { t } = useTranslation();
  const { badgesCount, creditsBalance } = useUserDataContext();
  const { data: badgeTypesData } = useAPIBadgeTypes();
  const [enabledListExpanded, setEnabledListExpanded] = useState<boolean>(getLocalStorageBooleanValue("menuEnabledListExpanded", true));
  const [disabledListExpanded, setDisabledListExpanded] = useState<boolean>(getLocalStorageBooleanValue("menuDisabledListExpanded", false));
  const [infoExpanded, setInfoExpanded] = useState<boolean>(getLocalStorageBooleanValue("menuInfoExpanded", true));

  const handleListExpanded = (list: "enabledList" | "disabledList" | "info") => {
    if (list === "enabledList") {
      localStorage.setItem("menuEnabledListExpanded", (!enabledListExpanded).toString());
      setEnabledListExpanded((prev) => !prev);
    } else if (list === "disabledList") {
      localStorage.setItem("menuDisabledListExpanded", (!disabledListExpanded).toString());
      setDisabledListExpanded((prev) => !prev);
    } else if (list === "info") {
      localStorage.setItem("menuInfoExpanded", (!infoExpanded).toString());
      setInfoExpanded((prev) => !prev);
    }
  };

  const getBadgesCount = () => {
    if (!badgeTypesData || !badgesCount) return undefined;

    return `${badgesCount} / ${badgeTypesData?.badgeTypes.length}`;
  };

  return (
    <Drawer
      variant={!!isSmallScreen ? "temporary" : "persistent"}
      open={drawerOpen}
      onClose={onItemClick}
      sx={{
        width: drawerOpen ? drawerWidth : 0,
        [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: "border-box" },
      }}>
      <Toolbar />
      <Box overflow={"auto"} flex={1}>
        <Stack direction={"column"} px={2} py={1.5}>
          <Stack direction={"column"} spacing={0.5}>
            {sidebarItems
              .filter((x) => !x.isSection)
              .map((item, i) => (
                <MenuItem key={"sidebar-item-" + i} icon={item.icon} title={t(item.id)} to={item.to} href={item.href} callback={onItemClick} withSelected />
              ))}
          </Stack>
          {userLoggedIn && (
            <>
              {botsList.enabledList.length ? (
                <AccordionItem
                  sx={{ pt: 1.5 }}
                  defaultExpanded
                  title={t("bottomNavigation.subMenu.STRATEGIES.title")}
                  expanded={enabledListExpanded}
                  onChange={() => handleListExpanded("enabledList")}>
                  <Stack direction={"column"} spacing={0.5}>
                    {botsList.enabledList}
                  </Stack>
                </AccordionItem>
              ) : undefined}
              {botsList.disabledList.length ? (
                <AccordionItem
                  sx={{ pt: 1.5 }}
                  title={t("bottomNavigation.subMenu.STRATEGIES.disabled")}
                  expanded={disabledListExpanded}
                  onChange={() => handleListExpanded("disabledList")}>
                  <Stack direction={"column"} spacing={0.5}>
                    {botsList.disabledList}
                  </Stack>
                </AccordionItem>
              ) : undefined}
              <AccordionItem
                sx={{ pt: 1.5 }}
                defaultExpanded
                title={t("bottomNavigation.info")}
                expanded={infoExpanded}
                onChange={() => handleListExpanded("info")}>
                <Stack direction={"column"} spacing={0.5}>
                  {sidebarItems
                    .filter((x) => x.isSection)
                    .map((item, i) => (
                      <MenuItem
                        key={"sidebar-item-info-" + i}
                        icon={item.icon}
                        title={t(item.id)}
                        to={item.to}
                        href={item.href}
                        withSelected
                      />
                    ))}
                </Stack>
              </AccordionItem>
            </>
          )}
        </Stack>
      </Box>
      {userLoggedIn && (
        <Box borderTop={`1px solid ${colors.gray200}`} bgcolor={colors.gray25} pt={1.5}>
          <ChallengeStatusCard />
          <Stack direction={"column"} spacing={0.5} px={0.4} pb={1}>
            <Stack px={2.2} py={1} justifyContent={"space-between"} direction={"row"} alignItems={"center"}>
              <MyAccountButton onClick={() => navigate("/nastaveni")}>
                <Typography color={colors.gray900} fontWeight={600} fontSize={"0.875rem"}>
                  {t("myAccount")}
                </Typography>
                <Settings1 sx={{ fontSize: 18 }} />
              </MyAccountButton>
              <SubscriptionBadge size="small" />
            </Stack>
            <MenuItem
              icon={<CreditBadge size="small" />}
              title={t("creditsAndSubscription")}
              to={"/stosaky"}
              badge={renderNumber(creditsBalance ?? 0)}
              withSelected
            />
            <MenuItem
              icon={<Award03 color="inherit" sx={{ fontSize: 20 }} />}
              title={t("myBadges")}
              to={"/stosaky#odznaky"}
              badge={getBadgesCount()}
              withSelected
            />
          </Stack>
        </Box>
      )}
    </Drawer>
  );
};

export default SidebarMenu;
